<template>
<div class="geo-item-search comment">
  <GeoItemSearchCommon
      v-model:search-params="commonSearchParams"
    :checkbox-key-prefix="checkboxKeyPrefix"
    :search-types="searchTypes"
    :search-type-map="searchTypeMap"
    :default-months-before="6"
    :max-search-range-months="6"
    @change="onChange"
  >
  </GeoItemSearchCommon>
  <div class="damage-rank">
    <span class="title-area" @click="state.showDamageRank = !state.showDamageRank">
      <i class="fas mr4" :class="{'fa-caret-right': !state.showDamageRank, 'fa-caret-down': state.showDamageRank}" />
      判定<span class="font-sm">{{selectedDamageRankDisp}}</span>
    </span>
    <div
      class="custom-control custom-checkbox ml15"
      v-for="option in COMMENT_DAMAGE_RANK_SEARCH_OPTIONS"
      :key="option.value"
      v-show="state.showDamageRank"
    >
      <input
        type="checkbox"
        class="custom-control-input"
        :id="`${checkboxKeyPrefix}-damage-rank-${option.value}`"
        :value="option.value"
        v-model="state.search.damageRanks"
        @change="onChange()"
      >
      <label class="custom-control-label" :for="`${checkboxKeyPrefix}-damage-rank-${option.value}`">
        {{option.label}}
      </label>
    </div>
  </div>
  <div class="share-scope">
    <span class="title-area" @click="state.showShareScope = !state.showShareScope">
      <i class="fas mr4" :class="{
        'fa-caret-right': !state.showShareScope,
        'fa-caret-down': state.showShareScope,
      }"></i>
      公開範囲<span class="font-sm">{{selectedShareScopeDisp}}</span>
    </span>
    <div class="form-group mb0 mr16 ml15" v-show="state.showShareScope">
      <select class="custom-select"
          v-model="state.search.shareScope"
          @change="onChange()">
        <option v-for="e in masters.lov.shareScopes" :key="e.key" :value="e.key">
          {{e.valAlt}}
        </option>
      </select>
    </div>
  </div>
  <div class="only-current-user">
    <span class="title-area" @click="state.showOnlyCurrentUser = !state.showOnlyCurrentUser">
      <i class="fas mr4" :class="{
        'fa-caret-right': !state.showOnlyCurrentUser,
        'fa-caret-down': state.showOnlyCurrentUser,
      }"></i>
      作成者<span class="font-sm">{{onlyCurrentUserDisp}}</span>
    </span>
    <div class="custom-control custom-checkbox ml15" v-show="state.showOnlyCurrentUser">
      <input type="checkbox" class="custom-control-input"
        id="only-current-user"
        v-model="state.search.onlyCurrentUser"
        @change="onChange()">
      <label class="custom-control-label" for="only-current-user">
        自分のみ
      </label>
    </div>
  </div>
</div>
</template>

<script setup lang="ts">
import {
  computed,
  onMounted,
  reactive,
  ref,
} from 'vue';
import GeoItemSearchCommon from 'src/components/Top/geoItemSearchComponents/GeoItemSearchCommon.vue';
import type { CommonSearchParams, SearchType, SearchTypeMap } from 'src/models/geoItemSearch';
import { useReactiveMasters } from 'src/composables/useMasters';
import type { CommentIndexParams } from 'src/models/apis/comment/commentRequest';
import { COMMENT_DAMAGE_RANK_SEARCH_OPTIONS, type CommentDamageRankSearch } from 'src/consts/comment_damage_rank';

interface SearchParams {
  shareScope: number | null;
  onlyCurrentUser: boolean;
  damageRanks: CommentDamageRankSearch[];
}

interface GeoItemSearchCommentState {
  showShareScope: boolean;
  showOnlyCurrentUser: boolean;
  showDamageRank: boolean;
  search: SearchParams;
}

const emit = defineEmits<{
  (e: 'change'): void;
}>();

const initSearchState = (): SearchParams => {
  return {
    shareScope: null,
    onlyCurrentUser: false,
    damageRanks: [],
  };
};
const state = reactive<GeoItemSearchCommentState>({
  showShareScope: false,
  showOnlyCurrentUser: false,
  showDamageRank: false,
  search: initSearchState(),
});
const { masters, resolveMastersPromise } = useReactiveMasters();

const searchTypes = computed<SearchType[]>(() => {
  return masters.comment.commentTypes.map(e => {
    return {'key': e.commentType, 'val': e.commentTypeDisp};
  });
});
const searchTypeMap = computed<SearchTypeMap>(() => {
  const map: SearchTypeMap = {};
  masters.comment.commentTypes.forEach(e => {
    map[e.commentType] = { val: e.commentTypeDisp, dispOrder: e.dispOrder };
  });
  return map;
});

const setInitialSearchParams = () => {
  const params = {
    shareScope: masters.lov.shareScopes[0].key,
    onlyCurrentUser: false,
    damageRanks: [],
  };
  state.search = params;
};

const selectedShareScopeDisp = computed<string>(() => {
  if (!state.search.shareScope) return '';

  const ret = masters.lov.shareScopeMap[state.search.shareScope].valAlt;
  return `（${ret}）`;
});
const onlyCurrentUserDisp = computed<string>(() => {
  const ret = state.search.onlyCurrentUser ? '自分のみ' : '全て';
  return `（${ret}）`;
});
const damageRankLabelMap = new Map(COMMENT_DAMAGE_RANK_SEARCH_OPTIONS.map(option => [option.value, option.label]));
const selectedDamageRankDisp = computed<string>(() => {
  if (state.search.damageRanks.length === 0) return '';

  const labels = state.search.damageRanks.map(value => damageRankLabelMap.get(value) ?? value);
  return `（${labels.join('、')}）`;
});
const commonSearchParams = ref<CommonSearchParams | null>(null);
const getSearchParams = (): CommentIndexParams | null => {
  if (!commonSearchParams.value) return null;

  const { searchTypes: commentTypes, ...other } = commonSearchParams.value;
  return {
    comment_types: commentTypes as string[],
    share_scope: state.search.shareScope,
    only_current_user: state.search.onlyCurrentUser ? 1 : 0,
    ts_from: other.tsFrom,
    ts_to: other.tsTo,
    road_name_disps: other.roadNameDisps,
    directions: other.directions,
    kp_calc_from: other.kpCalcFrom,
    kp_calc_to: other.kpCalcTo,
    damage_ranks: state.search.damageRanks,
  };
};
const onChange = () => {
  emit('change');
};

const checkboxKeyPrefix = 'comment';

onMounted(async() => {
  await resolveMastersPromise();
  setInitialSearchParams();
});

defineExpose({ getSearchParams });
</script>

<style lang="scss" scoped>
.geo-item-search {
  &.comment {
    margin: 0 0 0 25px;
  }
}
.title-area {
  cursor: pointer;
}
.font-sm {
  font-size: 12px;
}
.fa-caret-down {
  margin: 0 2px 0 -2px;
}
</style>
